import React, { useEffect, useState, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Container, Row, Col, Card, CardBody, Button, Label } from 'reactstrap';
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import "flatpickr/dist/themes/material_blue.css";
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine-dark.css';
import { getInitialDataRequest, getVesselPermitReportRequest, exportVesselPermitReportRequest, getCustomerVesselsRequest } from '../../../api/controller/VesselPermitReportController';
import { vesselPermitReportListState, vesselPermitReportCustomerState, vesselPermitReportVesselState, vesselPermitReportFilterState, vesselPermitReportDataStructureState, vesselPermitReportFromDateState, vesselPermitReportToDateState, vesselPermitReportCustomerValueState, vesselPermitReportVesselValueState } from './state';
import { Loader } from '../../../custom/components/Loader';
import { handleBlobResponse } from '../../../custom/helpers/HandleResponse';
import moment from "moment";

const VesselPermitReports = () => {
  const [filters, setFilters] = useRecoilState(vesselPermitReportFilterState);
  const [dataList, setDataList] = useRecoilState(vesselPermitReportListState);
  const setCustomerList = useSetRecoilState(vesselPermitReportCustomerState);
  const setVesselList = useSetRecoilState(vesselPermitReportVesselState);
  const dataStructure = useRecoilValue(vesselPermitReportDataStructureState);
  const setFromDate = useSetRecoilState(vesselPermitReportFromDateState);
  const setToDate = useSetRecoilState(vesselPermitReportToDateState);
  const setCustomerValue = useSetRecoilState(vesselPermitReportCustomerValueState);
  const setVesselValue = useSetRecoilState(vesselPermitReportVesselValueState);
  const [loading, setLoading] = useState(false);
  const [fetchingFilters, setFetchingFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const gridApiRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    async function fetchInitialData() {
      setFetchingFilters(true);
      const response = await getInitialDataRequest();
      if (response && response.Status !== 'error') {
        setCustomerList({
          data: [{ label: 'All', value: 0 }, ...(response.customers || [])],
          ovf: false,
        });
        setVesselList({
          data: [{ label: 'All', value: 0 }, ...(response.vessels || [])],
          ovf: false,
        });
      }
      setFetchingFilters(false);
    }
    fetchInitialData();
    
    // Cleanup function to reset data when component unmounts
    return () => {
      setDataList([]);
      setCustomerList({ data: [], ovf: false });
      setVesselList({ data: [], ovf: false });
      // Reset filters to default values
      setFromDate(moment().subtract(1, 'month').format('YYYY-MM-DD'));
      setToDate(moment().format('YYYY-MM-DD'));
      setCustomerValue({ label: "All", value: 0 });
      setVesselValue({ label: "All", value: 0 });
    };
    // eslint-disable-next-line
  }, []);

  const handleFilterChange = (filterLabel, value) => {
    setFilters({ filterLabel, value });
    
    // If customer changes, update vessels
    if (filterLabel === 'Customer') {
      updateVesselsByCustomer(value.value);
    }
  };

  const updateVesselsByCustomer = async (customerId) => {
    const response = await getCustomerVesselsRequest({ Customer: customerId });
    if (response && response.Status !== 'error') {
      setVesselList({
        data: [{ label: 'All', value: 0 }, ...(response.vessels || [])],
        ovf: false,
      });
      // Reset vessel selection to "All"
      setFilters({ filterLabel: 'Vessel', value: { label: 'All', value: 0 } });
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    const postData = {};
    filters.forEach(filter => {
      if (filter.value && filter.value !== 0) {
        if (filter.type === 'select') {
          postData[filter.label.replace(/ /g, '')] = filter.value.value;
        } else {
          postData[filter.label.replace(/ /g, '')] = filter.value;
        }
      }
    });
    const response = await getVesselPermitReportRequest(postData);
    if (response && response.Status !== 'error') {
      setDataList(response.data);
    }
    setLoading(false);
  };

  const exportReport = async () => {
    setExporting(true);
    const postData = {};
    filters.forEach(filter => {
      if (filter.value && filter.value !== 0) {
        if (filter.type === 'select') {
          postData[filter.label.replace(/ /g, '')] = filter.value.value;
        } else {
          postData[filter.label.replace(/ /g, '')] = filter.value;
        }
      }
    });
    const response = await exportVesselPermitReportRequest(postData);
    const fileName = `vessel_permit_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    handleBlobResponse(response, fileName);
    setExporting(false);
  };

  const columns = dataStructure.filter(f => f.order && f.order.table).map(f => ({
    headerName: f.label,
    field: f.field || f.label.replace(/ /g, '_').toLowerCase(),
    sortable: true,
    filter: true,
    resizable: true,
    flex: f.field === 'customer_name' ? 2 : 1,
    valueFormatter: (params) => {
      if (f.field === 'effective_at' || f.field === 'expires_at') {
        return params.value ? moment(params.value).format('MMM DD, YYYY HH:mm') : '';
      }
      return params.value;
    },
  }));

  return (
    <div className='page-content'>
      <Container fluid>
        <Row>
          <Col lg={12}>
            <div>
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="page-title">
                    <h4 className="mb-1 font-size-18">Vessel Permit Reports</h4>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="mb-4">
                  <h5 className="card-title mb-3">Filter Options</h5>
                  <Row>
                    {filters.map((filter, idx) => (
                      <Col key={filter.label} xs={12} md={filter.size || 6} className={filter.offset ? `offset-md-${filter.offset}` : ''}>
                        <div className="mb-3">
                          <Label className="form-label">{filter.label}</Label>
                          {filter.type === 'datepicker' && (
                            <Flatpickr
                              onChange={(_, e) => handleFilterChange(filter.label, e)}
                              placeholder={filter.label}
                              options={{ altInput: true, defaultDate: filter.value, dateFormat: 'Y-m-d' }}
                              value={filter.value}
                              className="form-control"
                            />
                          )}
                          {filter.type === 'select' && (
                            <Select
                              options={filter.list.data}
                              value={filter.value}
                              onChange={e => handleFilterChange(filter.label, e)}
                              isClearable={false}
                              placeholder={filter.label}
                            />
                          )}
                        </div>
                      </Col>
                    ))}
                    <Col xs={12} md={12} className="mt-3">
                      {!isMobile && (
                        <div className="d-flex flex-column flex-sm-row gap-2">
                          <Button color="primary" onClick={fetchReport} disabled={loading || fetchingFilters} className="me-sm-2 mb-2 mb-sm-0">
                            {loading ? 'Loading...' : 'Generate Report'}
                          </Button>
                          <Button color="success" onClick={exportReport} disabled={exporting || fetchingFilters}>
                            {exporting ? 'Exporting...' : 'Export Excel'}
                          </Button>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
                <div className="mt-4">
                  <h5 className="card-title mb-3">Report Results</h5>
                  {isMobile ? (
                    <div className="text-center py-4">
                      <div className="mb-3">
                        <i className="bx bx-table bx-lg text-muted"></i>
                      </div>
                      <h6 className="text-muted">Export Report Data</h6>
                      <p className="text-muted mb-3">Click the button below to download your filtered report as an Excel file.</p>
                      <Button color="success" onClick={exportReport} disabled={exporting || fetchingFilters} size="lg">
                        {exporting ? 'Exporting...' : 'Export Excel'}
                      </Button>
                    </div>
                  ) : (
                    <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                      <AgGridReact
                        ref={gridApiRef}
                        rowData={dataList}
                        columnDefs={columns}
                        defaultColDef={{ sortable: true, filter: true, resizable: true }}
                        domLayout="autoHeight"
                      />
                    </div>
                  )}
                  {loading && <Loader />}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VesselPermitReports; 